import { SortInput } from '@apeiron/common'
import { CONTRACT, ContractEventParams, useContract } from '@apeiron/contract'
import {
  CurrencyTokenType,
  useCurrencyContext,
  useGetOffChainTokenBalances
} from '@apeiron/currency'
import {
  DailyCheckInCampaign,
  DailyCheckInCampaignType,
  DailyCheckInClaimBonusInput,
  DailyCheckInClaimInput,
  DailyCheckInDay,
  DailyCheckInRewardEarnType,
  DailyCheckInRewardStatus,
  DailyCheckInShop,
  DailyCheckInShopSortBy
} from '@apeiron/reward'
import {
  createStrictContext,
  DEFAULT_LIST_SIZE,
  MayNull,
  PageInfo,
  PageInput,
  useAccountContext,
  useMessageContext,
  useWeb3
} from '@apeiron/temp'
import { ApeironPleaseWait } from '@apeiron/ui'
import { useMktErrorHandling } from '@apeiron/wallet'
import { BannerType } from '@src/constants/banner'
import { FeatureFlagType } from '@src/constants/featureFlag'
import { useNotificationContext } from '@src/contexts/share/NotificationContext'
import useGetMakeupPrice from '@src/hooks/contract/useGetMakeupPrice'
import useBuyDailyCheckInShopItem from '@src/hooks/graphql/useBuyDailyCheckInShopItem'
import useClaimDailyCheckIn from '@src/hooks/graphql/useClaimDailyCheckIn'
import useClaimDailyCheckInBonus from '@src/hooks/graphql/useClaimDailyCheckInBonus'
import useGetBanners from '@src/hooks/graphql/useGetBanners'
import useGetDailyCheckInShopItems from '@src/hooks/graphql/useGetDailyCheckInShopItems'
import useGetMyDailyCheckInCampaigns from '@src/hooks/graphql/useGetMyDailyCheckInCampaigns'
import { Banner } from '@src/types/banner'
import featureAllowed from '@src/util/apeiron/featureAllowed'
import * as R from 'ramda'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'

type DailyCheckInContextType = {
  banner: Banner[]
  dailyCheckInShopItem: DailyCheckInShop[]
  displayStardust: string
  myDailyCheckInCampaign: DailyCheckInCampaign[]
  stardust: string
  makeupPrices: string[]
  showMakeupPopup: boolean
  checkInOperation: (campaignID: string) => Promise<boolean>
  claimDailyCheckIn: (input: DailyCheckInClaimInput[]) => Promise<boolean>
  claimDailyCheckInBonus: (
    input: DailyCheckInClaimBonusInput[]
  ) => Promise<boolean>
  checkDiscordMakeUp: (campaign: MayNull<DailyCheckInCampaign>) => boolean
  fetchAfterOperation: () => Promise<void>
  fetchBanner: (params: { bannerTypes: BannerType[] }) => void
  fetchDailyCheckInShopItem: (params: {
    pageInput?: PageInput
    sortInput?: SortInput<DailyCheckInShopSortBy>
  }) => Promise<{ data: DailyCheckInShop[]; pageInfo: PageInfo }>
  fetchMyDailyCheckInCampaign: (params: { campaignTypes: string[] }) => void
  fetchStardust: () => void
  mutateBuyShopItem: (itemId: string) => Promise<boolean>
  setShowMakeupPopup: Dispatch<SetStateAction<boolean>>
  getMissedDaysCount: (campaign: MayNull<DailyCheckInCampaign>) => number
  getTotalMakeupableDaysCount: (
    campaign: MayNull<DailyCheckInCampaign>
  ) => number
  getCompletedMakeupDaysCount: (
    campaign: MayNull<DailyCheckInCampaign>
  ) => number
  getConsumeTokenCompletedMakeupDaysCount: (
    campaign: MayNull<DailyCheckInCampaign>
  ) => number
  fetchMakeupPrices: () => Promise<string[]>
}

const [ContextProvider, useDailyCheckInContext] =
  createStrictContext<DailyCheckInContextType>('DailyCheckIn')

export { useDailyCheckInContext }

export const DailyCheckInProvider = (props: Props) => {
  const { children } = props

  const { fetchBalances } = useAccountContext()

  const { getCurrency } = useCurrencyContext()

  const { setInfo } = useMessageContext()

  const { fetchNotifications } = useNotificationContext()

  const { fromWei } = useWeb3()

  const { showErrorPopup } = useMktErrorHandling()

  const { t } = useTranslation()

  const { grouped: balances, fetch: fetchStardust } =
    useGetOffChainTokenBalances()

  const { data: bannerData, fetch: fetchBanner } = useGetBanners()

  const { data: dailyCheckInShopItemData, fetch: fetchDailyCheckInShopItem } =
    useGetDailyCheckInShopItems()

  const { mutate: mutateBuyShopItem } = useBuyDailyCheckInShopItem()

  const { mutate: mutateClaimDailyCheckIn } = useClaimDailyCheckIn()

  const { mutate: mutateClaimDailyCheckInBonus } = useClaimDailyCheckInBonus()

  const {
    data: myDailyCheckInCampaignData,
    fetch: fetchMyDailyCheckInCampaign
  } = useGetMyDailyCheckInCampaigns()

  const { sendWithOption: contractCheckIn } = useContract(
    CONTRACT.DAILY_CHECK_IN.CHECK_IN
  )

  const { getMakeupPrice } = useGetMakeupPrice()

  const [makeupPrices, setMakeupPrices] = useState<string[]>([])

  const stardust = useMemo((): string => {
    const type = CurrencyTokenType.Stardust

    const wei = R.propOr('0', type, balances) as string

    return wei
  }, [balances])

  const displayStardust = useMemo((): string => {
    const type = CurrencyTokenType.Stardust

    const nonWei = fromWei(stardust, getCurrency(type)?.decimals || 1)

    return nonWei
  }, [fromWei, getCurrency, stardust])

  const [showMakeupPopup, setShowMakeupPopup] = useState<boolean>(false)

  const claimDailyCheckIn = useCallback(
    async (input: DailyCheckInClaimInput[]): Promise<boolean> => {
      try {
        const response = await mutateClaimDailyCheckIn(input)

        if (R.isNotNil(response?.isClaimedSuccess)) {
          await fetchMyDailyCheckInCampaign({
            campaignTypes: [DailyCheckInCampaignType.WeeklyStardust]
          })

          fetchNotifications()

          return true
        } else return false
      } catch (e) {
        return false
      }
    },
    [fetchMyDailyCheckInCampaign, fetchNotifications, mutateClaimDailyCheckIn]
  )

  const claimDailyCheckInBonus = useCallback(
    async (input: DailyCheckInClaimBonusInput[]): Promise<boolean> => {
      try {
        const response = await mutateClaimDailyCheckInBonus(input)

        if (response?.isClaimedSuccess) {
          await fetchMyDailyCheckInCampaign({
            campaignTypes: [DailyCheckInCampaignType.WeeklyStardust]
          })

          return true
        } else return false
      } catch (e) {
        return false
      }
    },
    [fetchMyDailyCheckInCampaign, mutateClaimDailyCheckInBonus]
  )

  const checkDiscordMakeUp = useCallback(
    (campaign: MayNull<DailyCheckInCampaign>): boolean => {
      if (R.isNotNil(campaign)) {
        const filtered = R.filter((day: DailyCheckInDay) => {
          return day.earnType === DailyCheckInRewardEarnType.MakeUp
        }, campaign.days)

        return filtered.length === 1
      }

      return false
    },
    []
  )

  const checkInOperation = useCallback(
    async (campaignID: string) => {
      try {
        setInfo({
          message: (
            <ApeironPleaseWait
              subject={t('common.popup.transaction.be_patient')}
            />
          )
        })

        await contractCheckIn(
          { verifyEvent: ContractEventParams.DailyCheckIn },
          campaignID
        )

        fetchNotifications()

        setInfo(null)

        return true
      } catch (error) {
        setInfo(null)
        showErrorPopup(error)
      }

      return false
    },
    [contractCheckIn, fetchNotifications, setInfo, showErrorPopup, t]
  )

  const fetchAfterOperation = useCallback(async () => {
    await Promise.all([
      fetchStardust(),
      fetchMyDailyCheckInCampaign({
        campaignTypes: [DailyCheckInCampaignType.WeeklyStardust]
      }),
      fetchNotifications(),
      fetchBalances()
    ])
  }, [
    fetchBalances,
    fetchMyDailyCheckInCampaign,
    fetchNotifications,
    fetchStardust
  ])

  const fetchMakeupPrices = useCallback((): Promise<string[]> => {
    return Promise.all(
      [1, 2, 3, 4, 5, 6].map(async (days: number) => {
        return getMakeupPrice(days)
      })
    ).then(prices => {
      setMakeupPrices(prices)

      return prices
    })
  }, [getMakeupPrice])

  useEffect(() => {
    fetchStardust()
    fetchBanner({ bannerTypes: [BannerType.DailyCheckIn] })
    fetchMyDailyCheckInCampaign({
      campaignTypes: [DailyCheckInCampaignType.WeeklyStardust]
    })
    fetchDailyCheckInShopItem({
      pageInput: { page: 1, pageSize: DEFAULT_LIST_SIZE }
    })
    if (featureAllowed(FeatureFlagType.ShowRewardMakeupCheckInByAnima)) {
      fetchMakeupPrices()
    }
  }, [])

  const getMissedDaysCount = useCallback(
    (campaign: MayNull<DailyCheckInCampaign>): number => {
      if (R.isNotNil(campaign)) {
        const countMissed = R.pipe<any, DailyCheckInDay[], number>(
          R.filter((day: DailyCheckInDay) => {
            return day.isPast && day.status === DailyCheckInRewardStatus.Locked
          }),
          R.length
        )

        return countMissed(campaign.days)
      }

      return 0
    },
    []
  )

  const getCompletedMakeupDaysCount = useCallback(
    (campaign: MayNull<DailyCheckInCampaign>): number => {
      if (R.isNotNil(campaign)) {
        const countMadeup = R.pipe<any, DailyCheckInDay[], number>(
          R.filter((day: DailyCheckInDay) => {
            return (
              (day.isPast &&
                day.earnType ===
                  DailyCheckInRewardEarnType.ConsumeTokenMakeUp) ||
              day.earnType === DailyCheckInRewardEarnType.MakeUp
            )
          }),
          R.length
        )

        return countMadeup(campaign.days)
      }

      return 0
    },
    []
  )

  const getConsumeTokenCompletedMakeupDaysCount = useCallback(
    (campaign: MayNull<DailyCheckInCampaign>): number => {
      if (R.isNotNil(campaign)) {
        const countMadeup = R.pipe<any, DailyCheckInDay[], number>(
          R.filter((day: DailyCheckInDay) => {
            return (
              day.isPast &&
              day.earnType === DailyCheckInRewardEarnType.ConsumeTokenMakeUp
            )
          }),
          R.length
        )

        return countMadeup(campaign.days)
      }

      return 0
    },
    []
  )

  const getTotalMakeupableDaysCount = useCallback(
    (campaign: MayNull<DailyCheckInCampaign>): number => {
      if (R.isNotNil(campaign)) {
        const countMissed = R.pipe<any, DailyCheckInDay[], number>(
          R.filter((day: DailyCheckInDay) => {
            return (
              day.isPast &&
              (day.status === DailyCheckInRewardStatus.Locked ||
                day.earnType ===
                  DailyCheckInRewardEarnType.ConsumeTokenMakeUp ||
                day.earnType === DailyCheckInRewardEarnType.MakeUp)
            )
          }),
          R.length
        )

        const makeupableDays = countMissed(campaign.days)

        return featureAllowed(FeatureFlagType.ShowRewardMakeupCheckInByAnima)
          ? makeupableDays
          : makeupableDays > 0
          ? 1
          : 0
      }

      return 0
    },
    []
  )

  return (
    <ContextProvider
      value={{
        banner: bannerData,
        dailyCheckInShopItem: dailyCheckInShopItemData,
        displayStardust,
        myDailyCheckInCampaign: myDailyCheckInCampaignData,
        showMakeupPopup,
        stardust,
        makeupPrices,
        checkInOperation,
        claimDailyCheckIn,
        claimDailyCheckInBonus,
        checkDiscordMakeUp,
        fetchAfterOperation,
        fetchBanner,
        fetchDailyCheckInShopItem,
        fetchMyDailyCheckInCampaign,
        fetchStardust,
        mutateBuyShopItem,
        setShowMakeupPopup,
        getMissedDaysCount,
        getTotalMakeupableDaysCount,
        getCompletedMakeupDaysCount,
        getConsumeTokenCompletedMakeupDaysCount,
        fetchMakeupPrices
      }}
    >
      {children}
    </ContextProvider>
  )
}

type Props = {
  children: ReactNode
}
